// ── PURCHASE ORDERS TAB ────────────────────────────────────────────────────────
// Flow: list → new1 (po_ref + project + vendor) → new2 (item picker) → detail
// Items eligible: Client Approved or Deposit Paid, isBillable (not parentId, not ABD)
// Lock point: 'Issued' — owner-only to edit after that
// On issue: all items on PO move to status 'PO Issued'

import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useDetailJump } from '../lib/jump';
import { useAppContext } from './AppContext';
import { Lbl, SHead, IS } from './ui';
import ConfirmationModal from './ConfirmationModal';
import VendorPicker from './VendorPicker';
import {
  itemNet,
  calcVendorDepositWithFees,
  defaultVendorDepositPct,
  parsePrice,
  parseQty,
  generatePORef,
  isBillable,
  projectLabelToCode,
  isPoEligibleStatus,
  STATUSES,
  itemsToRevertOnPODelete,
  canRevertPoToDraft,
  computePoStampChanges,
  forwardOnlyItemStatusUpdate,
  poNetLive,
  poDepositLive,
  fmtMoney,
  hasAdminAccess,
  displayNameFor,
  computeSidemark,
  computePoBypassShortfall,
  resolvePoShipTo,
  purchaseOrderStoragePath,
  computePoItemPaymentHistory,
  computePoFreightHistory,
  latestDocumentSend,
} from '../utils';

// ── CONSTANTS ─────────────────────────────────────────────────────────────────

var PO_STATUSES = ['Draft', 'Issued', 'Confirmed', 'Complete'];

function extractPOSeq(ref) {
  var m = (ref || '').match(/-PO-(\d+)$/i);
  return m ? parseInt(m[1], 10) : 0;
}

// No-fills rule (design guide / designTokens): hairline border + colored
// text on WHITE ground — never a tinted pill background. Confirmed is
// FUCHSIA (in flight with the vendor), Complete is GREEN — Cimon
// 2026-07-11: the two previously shared near-identical green fills and
// read the same at a glance. Hexes are the canonical zone tokens
// (ZONE_GREY / ZONE_FUCHSIA / ZONE_GREEN in designTokens.js).
var STATUS_STYLE = {
  'Draft':     { color: '#888880', border: '#D8D7D2' },
  'Issued':    { color: '#3730A3', border: '#A5B4FC' },
  'Confirmed': { color: '#D4007A', border: '#D4007A' },
  'Complete':  { color: '#1A5C38', border: '#1A5C38' },
};

// ── FORMATTING ────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch(e) { return iso.slice(0, 10); }
}

// ── BLANK PO ──────────────────────────────────────────────────────────────────

function blankPO() {
  return {
    id: crypto.randomUUID ? crypto.randomUUID() : (Date.now() + '-' + Math.random()),
    po_ref: '',
    project: '',
    vendorId: '',
    vendorName: '',
    status: 'Draft',
    items: [],
    version: 1,
    total_net: 0,
    total_deposit: 0,
    notes: '',
    created_at: new Date().toISOString(),
    issued_at: null,
  };
}

// ── STYLES ────────────────────────────────────────────────────────────────────

var S = {
  wrap:      { fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: '#111111', background: '#FFFFFF' },
  toolbar:   { display: 'flex', alignItems: 'center', height: 44, padding: '0 14px', gap: 8, borderBottom: '0.5px solid #D8D7D2', background: '#F7F7F5' },
  backBtn:   { background: 'none', border: 'none', fontSize: 11, color: '#888880', cursor: 'pointer', fontFamily: 'inherit', padding: 0, flexShrink: 0 },
  slash:     { color: '#D8D7D2', fontSize: 11, flexShrink: 0 },
  pageTitle: { fontSize: 13, fontWeight: 500, color: '#111111', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tbtnGhost:   { height: 28, padding: '0 10px', borderRadius: 5, border: '0.5px solid #D8D7D2', background: '#FFFFFF', cursor: 'pointer', fontSize: 11, fontWeight: 500, color: '#111111', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  tbtnPrimary: { height: 28, padding: '0 11px', borderRadius: 5, border: '0.5px solid #111111', background: '#111111', cursor: 'pointer', fontSize: 11, fontWeight: 500, color: '#FFFFFF', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  tbtnDanger:  { height: 28, padding: '0 10px', borderRadius: 5, border: '0.5px solid #C8322A', background: '#FFFFFF', cursor: 'pointer', fontSize: 11, fontWeight: 500, color: '#C8322A', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  ctxStrip:  { display: 'flex', alignItems: 'center', minHeight: 34, padding: '0 14px', borderBottom: '0.5px solid #D8D7D2', background: '#FFFFFF', flexWrap: 'wrap', gap: 0, fontSize: 11, color: '#555552' },
  ctxPill:   { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px 6px 0', borderRight: '0.5px solid #EFEFEC', marginRight: 12 },
  ctxLabel:  { fontSize: 10, color: '#888880' },
  content:   { padding: '20px 14px', background: '#FFFFFF' },
};

// ── STATUS BADGE ──────────────────────────────────────────────────────────────

function StatusPill({ status }) {
  var s = STATUS_STYLE[status] || STATUS_STYLE['Draft'];
  return (
    <span style={{ background: '#FFFFFF', color: s.color, border: '0.5px solid ' + s.border, padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 500, letterSpacing: '0.04em', flexShrink: 0 }}>
      {status}
    </span>
  );
}

// ── TABLE HEADER CELL ─────────────────────────────────────────────────────────

function TH({ children, right }) {
  return (
    <th style={{ padding: '6px 10px', textAlign: right ? 'right' : 'left', fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', whiteSpace: 'nowrap', borderBottom: '0.5px solid #D8D7D2', background: '#F7F7F5' }}>
      {children}
    </th>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

export default function PurchaseOrdersTab() {
  var ctx = useAppContext();
  var purchaseOrders = ctx.purchaseOrders || [];
  var setPurchaseOrders = ctx.setPurchaseOrders;
  var allItems = ctx.items || [];
  var vendors = ctx.vendors || [];
  var projects = ctx.projects || [];
  var updateItem = ctx.updateItem;
  var isOwner = ctx.isOwner;
  var savePOToDb = ctx.savePOToDb;
  var deletePOFromDb = ctx.deletePOFromDb;
  var dispenseDocSequence = ctx.dispenseDocSequence;
  var autoAssignItemNumber = ctx.autoAssignItemNumber;
  var firmSettings = ctx.firmSettings || {};
  // Allocation-aware shortfall (since 2026-05-25): the PO bypass gate
  // reads payment_allocations against the matched deposit invoice for
  // each item, not the item.status proxy.
  var allInvoices = ctx.invoices || [];
  var paymentAllocations = ctx.paymentAllocations || [];

  var vs = useState('list');   var view = vs[0];          var setView = vs[1];
  var ds = useState(null);     var detailId = ds[0];       var setDetailId = ds[1];

  // Honor detail jumps from other tabs.
  useDetailJump('purchase-orders', function(id) { setDetailId(id); setView('detail'); });
  var ns = useState(blankPO()); var newPO = ns[0];         var setNewPO = ns[1];
  var ss = useState([]);       var selectedIds = ss[0];    var setSelectedIds = ss[1];
  var dcs = useState(false);   var deleteConfirm = dcs[0]; var setDeleteConfirm = dcs[1];
  var pls = useState(false);   var pdfLoading = pls[0];    var setPdfLoading = pls[1];
  var pes = useState('');      var pdfErr = pes[0];        var setPdfErr = pes[1];
  var pvs = useState(null);    var pdfPreviewUrl = pvs[0]; var setPdfPreviewUrl = pvs[1];
  var pvf = useState('');      var pdfPreviewFilename = pvf[0]; var setPdfPreviewFilename = pvf[1];
  // Phase 5A.2 — Live/Original toggle in the PDF preview modal.
  // pdfPreviewPoId tracks which PO we're previewing so the toggle can look
  // up the most-recent document_sends row for the Original view.
  var ppids = useState(null);  var pdfPreviewPoId = ppids[0]; var setPdfPreviewPoId = ppids[1];
  var ppms = useState('live'); var pdfPreviewMode = ppms[0]; var setPdfPreviewMode = ppms[1];
  var ppos = useState(null);   var pdfPreviewOriginalUrl = ppos[0]; var setPdfPreviewOriginalUrl = ppos[1];
  var ppolds = useState(false); var pdfPreviewOriginalLoading = ppolds[0]; var setPdfPreviewOriginalLoading = ppolds[1];
  var sas = useState({});      var skuAssigning = sas[0];  var setSkuAssigning = sas[1];
  var pfs = useState('');      var projFilter = pfs[0];    var setProjFilter = pfs[1];
  // Status pills (2026-07-12, Cimon): All / In Process / Complete.
  // "In Process" = Draft + Issued + Confirmed — everything still moving.
  var sfs = useState('all');   var statusFilter = sfs[0];  var setStatusFilter = sfs[1];
  var pqs = useState('');      var poSearch = pqs[0];      var setPoSearch = pqs[1];
  var exs = useState({});      var expandedItems = exs[0];  var setExpandedItems = exs[1];
  // Issue PO confirmation modal. Holds {po} when open, null otherwise.
  // The modal intercepts the Draft→Issued advance so every PO send goes
  // through a verification beat (quote vs. vendor invoice, dimensions,
  // finishes). On confirm: advanceStatus then downloadPoPdf, neither
  // blocked on the other.
  var ims = useState(null);    var issueModal = ims[0];    var setIssueModal = ims[1];
  var imcs = useState(false);  var issueConfirming = imcs[0]; var setIssueConfirming = imcs[1];
  // SPEC 1 — soft-warning bypass modal. Fires when the user clicks
  // "I've Verified — Send" on the IssueModal AND one or more items on
  // the PO are still at Client Approved (i.e., no client deposit
  // recorded). Owner can override with "Issue anyway"; non-owners
  // can only "Wait — record payment first." See computePoBypassShortfall
  // in utils.js for the calculation and the bypass-trigger SQL in
  // supabase/migrations/20260521_po_bypass_columns.sql for the server-
  // side defense.
  var bms = useState(null);    var bypassModal = bms[0];   var setBypassModal = bms[1];
  var rvm = useState(null);    var revertModal = rvm[0];   var setRevertModal = rvm[1];
  var rve = useState('');      var revertErr = rve[0];     var setRevertErr = rve[1];
  var rvb = useState(false);   var reverting = rvb[0];     var setReverting = rvb[1];
  // Owner + admin manage locked POs (edit, edit items, revert). Veronica
  // does the ordering (Cimon, 2026-10-21).
  var canManagePo = isOwner || hasAdminAccess(ctx.userEmail, ctx.firmSettings);
  var filterInitRef = useRef(false);

  // Auto-select first project on initial data load
  useEffect(function() {
    if (filterInitRef.current) return;
    var projs = Array.from(new Set(
      purchaseOrders.map(function(po) { return po.project || ''; })
        .concat(allItems.filter(function(it) { return it.status === 'Client Approved' || it.status === 'Deposit Paid'; }).map(function(it) { return it.project || ''; }))
        .filter(Boolean)
    )).sort();
    if (projs.length > 0) {
      filterInitRef.current = true;
      setProjFilter(projs[0]);
    }
  }, [purchaseOrders.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── HELPERS ────────────────────────────────────────────────────────────────

  function vendorDepPct(it) {
    return parseFloat(it.depositPct) || defaultVendorDepositPct(it.leadTime);
  }

  // Per-item vendor deposit contribution. Installation rides on the PO
  // when item.fees.installation > 0 — the fee on the item IS the marker
  // (since 2026-06-03; the vendor-level installs_own_goods flag was
  // dropped because installation is a property of the item, not the
  // vendor).
  function itemDep(it) {
    return calcVendorDepositWithFees(
      parsePrice(it.netPrice),
      parseQty(it.qty),
      vendorDepPct(it),
      it.fees || {}
    );
  }

  // Compact product-name label for table rows. Accepts an array of full
  // item objects (Ready to Purchase groups) OR an array of item-id
  // strings (existing-PO rows — `po.items` is ids). For single-item
  // rows shows the name; multi-item shows first name + "(+N more)".
  function productLabel(items) {
    if (!items || items.length === 0) return '—';
    var first = items[0];
    var firstObj = (first && typeof first === 'object')
      ? first
      : allItems.find(function(x) { return x.id === first; });
    if (!firstObj) return '—';
    var name = firstObj.item || firstObj.productName || '—';
    if (items.length > 1) name += ' (+' + (items.length - 1) + ' more)';
    return name;
  }

  // isPoEligibleStatus imported from ../utils — single source of truth.

  // Items already attached to any PO. Used to exclude those from new-PO
  // eligibility -- otherwise the same item could be added to two POs.
  function buildAlreadyPoIdSet() {
    var s = new Set();
    (purchaseOrders || []).forEach(function(po) {
      (po.items || []).forEach(function(id) { s.add(id); });
    });
    return s;
  }

  // Vendors that have items eligible for a NEW PO (correct status, in this
  // project, and not already on an existing PO). Returns vendor directory
  // objects where available; synthetic objects for ad-hoc names that don't
  // match a directory record.
  function eligibleVendors(projectLabel) {
    var alreadyPo = buildAlreadyPoIdSet();
    var seen = new Set();
    var result = [];
    allItems.forEach(function(it) {
      if (alreadyPo.has(it.id)) return;
      if (isBillable(it) && isPoEligibleStatus(it.status)) {
        if (!projectLabel || it.project === projectLabel) {
          if (it.vendor && !seen.has(it.vendor)) {
            seen.add(it.vendor);
            var dirEntry = vendors.find(function(v) { return v.name === it.vendor; });
            result.push(dirEntry || { id: it.vendor, name: it.vendor });
          }
        }
      }
    });
    return result;
  }

  // Items eligible for a NEW PO: Deposit Paid status, in this project, for
  // this vendor, and not already on an existing PO.
  function eligibleItems(projectLabel, vendorName) {
    var alreadyPo = buildAlreadyPoIdSet();
    return allItems.filter(function(it) {
      return isBillable(it)
        && isPoEligibleStatus(it.status)
        && !alreadyPo.has(it.id)
        && (!projectLabel || it.project === projectLabel)
        && (!vendorName || it.vendor === vendorName);
    });
  }

  // Stored row totals. `total_net` is product-only ("net" = goods value).
  // `total_deposit` mirrors the PDF deposit math, including per-item
  // installation when fees.installation > 0 (the vendor-level flag was
  // dropped 2026-06-03).
  function calcTotals(itemIds) {
    var its = allItems.filter(function(it) { return itemIds.indexOf(it.id) >= 0; });
    var net = its.reduce(function(s, it) { return s + itemNet(it); }, 0);
    var dep = its.reduce(function(s, it) { return s + itemDep(it); }, 0);
    return {
      total_net:     Math.round(net * 100) / 100,
      total_deposit: Math.round(dep * 100) / 100,
    };
  }

  // Optimistically inserts/updates the PO in local state, then awaits the
  // DB save. If the save fails, restores the previous local state so the
  // UI doesn't lie to the user (the PO won't appear saved when it isn't —
  // the 2026-05-08 silent-save bug shape). The error banner surfaces via
  // window._setSaveError inside savePOToDb.
  async function savePO(p) {
    // Keep items.poNumber in sync with this PO's items array. Two
    // symmetric paths:
    //   - Remove: clear stamps on items detached from this PO (Bug 2
    //     fix, 2026-06-10).
    //   - Add: stamp this PO's ref on items attached to this Issued+ PO
    //     (2026-06-12 fix for the 26-Moskito stamp-drift incident).
    // Pure logic in computePoStampChanges (utils.js).
    var prevPO = purchaseOrders.find(function(x) { return x.id === p.id; });
    if (prevPO && updateItem) {
      var stampOps = computePoStampChanges(prevPO, p, allItems);
      stampOps.forEach(function(op) {
        // Warn when we're overwriting a non-empty stale stamp on add —
        // signals the prior PO's remove-path didn't clear (pre-Bug-2
        // history or another bug). The overwrite is the right move;
        // the warning is a forensic signal for the integrity sweep.
        if (op.patch.poNumber) {
          var it = (allItems || []).find(function(x) { return x.id === op.id; });
          if (it && it.poNumber && it.poNumber !== op.patch.poNumber) {
            console.warn(
              'savePO: overwriting stale poNumber on item', op.id,
              'from', it.poNumber, 'to', op.patch.poNumber
            );
          }
        }
        try { updateItem(op.id, op.patch); }
        catch (e) { console.error('savePO: poNumber stamp change failed for', op.id, e); }
      });
    }

    var snapshot = null;
    setPurchaseOrders(function(prev) {
      snapshot = prev;
      var exists = prev.some(function(x) { return x.id === p.id; });
      if (exists) return prev.map(function(x) { return x.id === p.id ? p : x; });
      return prev.concat([p]);
    });
    if (!savePOToDb) return;
    var ok = await savePOToDb(p);
    if (!ok && snapshot !== null) {
      setPurchaseOrders(snapshot);
    }
  }

  async function deletePO(id) {
    var toDelete = purchaseOrders.find(function(x) { return x.id === id; });
    if (!toDelete) {
      window.alert('PO not found.');
      return;
    }
    // Guards: block delete on POs that have crossed real-world thresholds.
    // Once a PO is Issued, the vendor has been notified and downstream
    // accounting (Bills, vendor_payments) may reference it. Deleting
    // silently breaks the audit trail.
    if (toDelete.status && toDelete.status !== 'Draft') {
      window.alert('Cannot delete PO ' + (toDelete.po_ref || id) + ' — it has been ' + toDelete.status + '. Use Revert to Draft first, then delete. If goods have shipped, cancel items via the cancel-with-credit flow instead.');
      return;
    }
    // Block if any vendor receipt or vendor_payment references this PO.
    var vendorPayments = ctx.vendorPayments || [];
    var poRef = toDelete.po_ref || '';
    var paymentsRef = vendorPayments.filter(function(p){
      if (p && p.poId && String(p.poId) === String(id)) return true;
      return poRef && String(p.reference || '').indexOf(poRef) >= 0;
    });
    if (paymentsRef.length > 0) {
      var paid = paymentsRef.reduce(function(s, p){ return s + (parseFloat(p.amount) || 0); }, 0);
      window.alert('Cannot delete PO ' + poRef + ' — $' + paid.toFixed(2) + ' in vendor payments are recorded against it. Reverse the payment(s) first.');
      return;
    }
    var receipts = ctx.receipts || [];
    var receiptsForPo = receipts.filter(function(r){ return r.poId === id; });
    if (receiptsForPo.length > 0) {
      window.alert('Cannot delete PO ' + poRef + ' — ' + receiptsForPo.length + ' receipt(s) are linked to it.');
      return;
    }

    if (updateItem) {
      var ops = itemsToRevertOnPODelete(toDelete, allItems);
      var results = await Promise.all(ops.map(function(op) {
        return updateItem(op.id, op.patch);
      }));
      var failed = results.filter(function(r) { return r === false; }).length;
      if (failed > 0) {
        console.error('deletePO: ' + failed + ' of ' + ops.length + ' item reverts failed');
      }
    }
    setPurchaseOrders(function(prev) { return prev.filter(function(p) { return p.id !== id; }); });
    if (deletePOFromDb) deletePOFromDb(id);
    setView('list');
    setDeleteConfirm(false);
  }

  // Shared payload + filename builder used by both the preview-modal
  // surface (previewPdf) and the programmatic download surface
  // (downloadPoPdf, triggered by the Issue PO confirmation flow). Keeping
  // the data-building in one place ensures the preview and the sent PDF
  // are byte-identical from the same PO state.
  // buildPoPdfPayload(p, options)
  //   options.includeAnnotations — Phase 5A.2 (2026-06-25). When true, the
  //     payload includes per-item payment history + per-PO freight history
  //     as `annotations`. The Python renderer reads these to draw red-ink
  //     payment-progress lines under each item and after the FREIGHT row.
  //     Live preview path always passes true; the Issue PO snapshot
  //     deliberately omits annotations so the frozen Original is the
  //     vendor-facing PDF without payment markup.
  function buildPoPdfPayload(p, options) {
    var includeAnnotations = !!(options && options.includeAnnotations);
    var vendor = vendors.find(function(v) { return v.name === p.vendorName || String(v.id) === String(p.vendorId); }) || {};
    var resolvedVendorName = p.vendorName || vendor.name || '';
    // Installation rule (since 2026-06-03): per-item via fees.installation.
    // If an item has fees.installation > 0, its install rides on this PO at
    // net cost. No vendor-level flag. The fee on the item IS the marker.
    var proj = projects.find(function(pr) { return pr.label === p.project; }) || {};
    var vendorAddr = [vendor.email, vendor.phone].filter(Boolean).join(' \u00b7 ');
    var siteAddr = [proj.address, proj.address2, [proj.city, proj.state, proj.zip].filter(Boolean).join(', ')].filter(Boolean).join('\n');
    // Ship-to: item-level overrides win over the project receiver. When the
    // items on this PO disagree on ship-to, the helper returns the first
    // override and a conflict array — surface that warning so the bookkeeper
    // can split the PO before sending.
    var shipToResult = resolvePoShipTo(p, allItems, vendors, projects);
    var shipAddr = shipToResult.text;
    if (shipToResult.conflict) {
      console.warn(
        '[PO ' + (p.po_ref || p.id) + '] items disagree on ship-to address:',
        shipToResult.conflict,
        '\u2192 PDF will use the first one (' + shipToResult.text + '). Consider splitting into separate POs.'
      );
    }
    var poItems = allItems.filter(function(it) { return (p.items || []).indexOf(it.id) >= 0; });
    function fp(s) { var n = parseFloat((s||'').toString().replace(/[$,]/g,'')); return isNaN(n) ? 0 : n; }
    var lineItems = poItems.map(function(it, i) {
      var sidemark = computeSidemark(p.project, it.roomName, it.roomNumber);
      var fees = it.fees || {};
      var depPctIt = vendorDepPct(it);
      var productDepositAmt = Math.round(parsePrice(it.netPrice) * parseQty(it.qty) * depPctIt / 100 * 100) / 100;
      var feeCustom = (fees.custom || []).filter(function(c) { return fp(c.amount) > 0; })
        .map(function(c) { return { label: c.label || 'Custom Fee', amount: fp(c.amount) }; });
      return {
        // Phase 5A.2 — id flows to Python renderer so it can look up
        // annotations.items[id] when rendering the Live (annotated) view.
        id: it.id,
        num: i + 1,
        item: it.item || 'Untitled',
        productName: it.productName || '',
        description: it.specNotes || '',
        model: it.model || '',
        finish: it.finish || '',
        dimensions: it.dimensions || '',
        qty: String(parseQty(it.qty) || 1),
        qtyType: it.qtyType || 'Each',
        unitCost: parsePrice(it.netPrice),
        total: itemNet(it),
        leadTime: it.leadTime || 'TBD',
        sidemark: sidemark,
        images: it.images || [],
        heroIndex: it.heroIndex || 0,
        depositAmt: productDepositAmt,
        vendorDepPct: depPctIt,
        feeFreight:  fp(fees.freight),
        feeCrating:  fp(fees.crating),
        // feeInstallation flows directly from the item's installation
        // fee. The fee on the item is the marker — items without an
        // installation fee contribute $0 here, items with one ride on
        // the PO at net cost (2026-06-03 per-item rule).
        feeInstallation: fp(fees.installation),
        feeTariff:   fp(fees.tariff),
        // Sales tax intentionally NOT on PO. The firm never pays sales
        // tax to the vendor — sales tax is collected from the client
        // (via the FIN's tax line) and remitted directly to the state.
        // CLAUDE.md "Vendor PO scope": sales tax is NEVER on a vendor PO.
        // See also resale certificate flow — the firm presents a resale
        // cert so the vendor doesn't even collect tax on the PO itself.
        feeCustom:   feeCustom,
      };
    });
    var totalProductNet = poItems.reduce(function(s, it) { return s + itemNet(it); }, 0);
    var totalFeeFreight  = poItems.reduce(function(s, it) { return s + fp((it.fees||{}).freight); }, 0);
    var totalFeeCrating  = poItems.reduce(function(s, it) { return s + fp((it.fees||{}).crating); }, 0);
    var totalFeeInstallation = poItems.reduce(function(s, it) { return s + fp((it.fees||{}).installation); }, 0);
    var totalFeeTariff   = poItems.reduce(function(s, it) { return s + fp((it.fees||{}).tariff); }, 0);
    // Sales tax intentionally NOT aggregated for PO totals — see the
    // per-item comment above. The firm collects from the client and
    // remits to the state; the vendor never sees a tax line on the PO.
    var customFeeMap = {};
    poItems.forEach(function(it) {
      ((it.fees||{}).custom||[]).forEach(function(c) {
        var amt = fp(c.amount);
        if (amt > 0) { var lbl = c.label || 'Custom Fee'; customFeeMap[lbl] = (customFeeMap[lbl]||0) + amt; }
      });
    });
    var totalFeeCustom = Object.keys(customFeeMap).map(function(lbl) { return { label: lbl, amount: customFeeMap[lbl] }; });
    var totalFees = totalFeeFreight + totalFeeCrating + totalFeeInstallation + totalFeeTariff + totalFeeCustom.reduce(function(s, c) { return s + c.amount; }, 0);
    var grandSubtotal = totalProductNet + totalFees;
    // Per-item deposit contribution. For vdp < 100 items, deposit is
    // product net × dPct only. For vdp = 100 items (in-stock), the
    // vendor expects product + freight + crating + tariff + custom
    // fees upfront because there's no final invoice cycle for that
    // item — calcVendorDepositWithFees encodes this rule (utils.js:
    // 1283-1290). Installation excluded (separate installer) and
    // salesTax excluded (resale certificate) by the helper.
    //
    // Pre-fix this site computed product × dPct / 100 only, omitting
    // fees on 100%-vdp items — the PO Deposit Total under-instructed
    // vendors by the missing fee amount. Backfilled 2026-05-28 for 1
    // stale row (26HOB-PO-012); 6 other PO rows already had correct
    // stored values, only their PDF re-render path was wrong. See
    // Fix F commit message + /tmp/fee-flow-audit-20260528.md.
    var totalProductDeposit = poItems.reduce(function(s, it) {
      var dPct = vendorDepPct(it);
      return s + calcVendorDepositWithFees(it.netPrice, it.qty, dPct, it.fees);
    }, 0);
    var depPct = totalProductNet > 0 ? Math.round(totalProductDeposit / totalProductNet * 100) : 50;
    var fname = (p.po_ref || 'draft').replace(/[^a-zA-Z0-9-]/g, '') + '.pdf';
    var payload = {
      poNumber: p.po_ref,
      vendorName: resolvedVendorName,
      vendorAddr: vendorAddr,
      shipAddr: shipAddr,
      siteAddr: siteAddr,
      lineItems: lineItems,
      productNet: Math.round(totalProductNet * 100) / 100,
      feeFreight:  Math.round(totalFeeFreight * 100) / 100,
      feeCrating:  Math.round(totalFeeCrating * 100) / 100,
      // feeInstallation aggregated across all items with fees.installation > 0.
      // Python renderer renders the labor line only when > 0 (since 2026-06-03
      // per-item rule; vendor-level flag dropped).
      feeInstallation: Math.round(totalFeeInstallation * 100) / 100,
      feeTariff:   Math.round(totalFeeTariff * 100) / 100,
      // feeSalesTax intentionally omitted from PO payload — see comments
      // in the per-item lineItems block above.
      feeCustom:   totalFeeCustom,
      subtotal: Math.round(grandSubtotal * 100) / 100,
      depositPct: depPct,
      // Live deposit total -- the stored p.total_deposit goes stale when
      // items are re-priced after PO save; the locally-computed
      // totalProductDeposit keeps PDF math consistent with the per-item
      // math above.
      depositAmount: Math.round(totalProductDeposit * 100) / 100,
      terms: 'For items not currently in stock, deposit payments do not include applicable shipping, crating, freight, taxes, or tariffs; these charges will be invoiced upon completion of the order.',
      notes: p.notes || '',
      projectLabel: p.project,
      projectDisplay: proj.displayName || proj.label || p.project,
      // Bug 3 fix (2026-06-26): stamp the PO's actual issue date in the
      // header, not today's calendar date. Drafts (no issued_at) fall
      // back to created_at, then today. Mirrors the logic that the
      // snapshot backfill (scripts/backfill-po-document-sends.mjs) has
      // had all along — the live PDF builder had drifted away from it.
      date: new Date(p.issued_at || p.created_at || Date.now()).toLocaleDateString('en-US'),
      firmName: firmSettings.name || 'Cimon Starr LLC',
      firmStreet: firmSettings.address || '3516 Sacramento Street',
      firmCity: firmSettings.city || 'San Francisco',
      firmState: firmSettings.state || 'CA',
      firmZip: firmSettings.zip || '94118',
    };
    // Phase 5A.2 — Live annotations payload. When requested, attach
    // per-item payment history + per-PO freight history. Python
    // renderer reads `annotations` to draw the red-ink overlays.
    if (includeAnnotations) {
      var receipts = ctx.receipts || [];
      var vendorPayments = ctx.vendorPayments || [];
      var vendorPaymentItems = ctx.vendorPaymentItems || [];
      payload.annotations = {
        items:   computePoItemPaymentHistory(p, allItems, vendorPayments, vendorPaymentItems, vendors),
        freight: computePoFreightHistory(p, vendorPayments, vendorPaymentItems, receipts, vendors),
      };
    }
    return { payload: payload, fname: fname };
  }

  function previewPdf(p) {
    setPdfLoading(true); setPdfErr('');
    // Phase 5A.2 — Preview always uses the annotated Live view.
    var pkg = buildPoPdfPayload(p, { includeAnnotations: true });
    setPdfPreviewPoId(p.id);
    setPdfPreviewMode('live');
    fetch('/api/generate-po-pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pkg.payload),
    })
    .then(function(res) {
      if (!res.ok) return res.json().then(function(d) { throw new Error(d.error || 'PDF failed'); });
      return res.blob();
    })
    .then(function(blob) {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(URL.createObjectURL(blob));
      setPdfPreviewFilename(pkg.fname);
      setPdfLoading(false);
    })
    .catch(function(e) { setPdfErr(e.message || 'PDF generation failed'); setPdfLoading(false); });
  }

  // Trigger a programmatic file download of the PO PDF — used by the
  // Issue PO confirmation flow after status advance succeeds. Reuses the
  // same payload as previewPdf but consumes the blob as a download click
  // instead of an iframe URL. Failure surfaces in pdfErr as a banner but
  // never throws or blocks the caller: the PO status change has already
  // committed, the bookkeeper can retry the download via "Preview PDF"
  // and the toolbar's "↓ Download" button.
  //
  // Phase 5A.2 (2026-06-25): opts.captureSnapshot=true also uploads the
  // rendered PDF to the purchase-orders bucket and writes a document_sends
  // row freezing the payload + storage path. Triggered from the Issue PO
  // confirmation flows (regular + bypass). The toolbar Download button
  // continues to call this without captureSnapshot so re-downloading a
  // PO that's already Issued doesn't append more document_sends rows.
  function downloadPoPdf(p, opts) {
    setPdfErr('');
    var captureSnapshot = !!(opts && opts.captureSnapshot);
    var pkg = buildPoPdfPayload(p);
    return fetch('/api/generate-po-pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pkg.payload),
    })
      .then(function(res) {
        if (!res.ok) return res.json().then(function(d) { throw new Error(d.error || 'PDF failed'); });
        return res.blob();
      })
      .then(async function(blob) {
        // Phase 5A.2 — snapshot capture. Best-effort: failure here never
        // blocks the user download. The Issue PO status change has
        // already committed; the snapshot write is durability for the
        // toggle's Original view + the audit trail.
        if (captureSnapshot && ctx && ctx.recordDocumentSend) {
          try {
            var sentAt = new Date();
            var storagePath = purchaseOrderStoragePath(p.id, sentAt);
            var relPath = storagePath.replace(/^purchase-orders\//, '');
            var up = await supabase.storage
              .from('purchase-orders')
              .upload(relPath, blob, { contentType: 'application/pdf', upsert: false });
            if (up.error) {
              console.warn('PO PDF upload to storage failed:', up.error.message);
            }
            // Resolve vendor email — best-effort; missing email is fine.
            var vendor = (ctx.vendors || []).find(function(v) {
              return v && (String(v.id) === String(p.vendorId) || String(v.id) === String(p.vendor_id));
            });
            await ctx.recordDocumentSend({
              documentType: 'po',
              documentId:   p.id,
              payload:      pkg.payload,
              storagePath:  up.error ? '' : storagePath,
              sentTo:       (vendor && (vendor.email || vendor.contact)) || '',
            });
          } catch (sendErr) {
            console.warn('document_sends write for PO failed:', sendErr && sendErr.message);
          }
        }

        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = pkg.fname;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Give the browser a moment to start the download before
        // revoking — without the delay, some browsers cancel the
        // download mid-flight.
        setTimeout(function() { URL.revokeObjectURL(url); }, 1000);
      })
      .catch(function(e) {
        setPdfErr('PDF download failed: ' + (e.message || 'unknown error') + ' \u2014 status was still updated.');
      });
  }

  function isLocked(p) {
    var idx = PO_STATUSES.indexOf(p.status);
    return idx >= PO_STATUSES.indexOf('Issued');
  }

  function assignSku(it) {
    if (!autoAssignItemNumber || skuAssigning[it.id]) return;
    setSkuAssigning(function(prev) { return Object.assign({}, prev, { [it.id]: true }); });
    autoAssignItemNumber(it).then(function() {
      setSkuAssigning(function(prev) { var n = Object.assign({}, prev); delete n[it.id]; return n; });
    }).catch(function() {
      setSkuAssigning(function(prev) { var n = Object.assign({}, prev); delete n[it.id]; return n; });
    });
  }

  // One-click create from a pending group — auto-ref, all items, straight to detail
  function createDraftAndNavigate(g) {
    var projObjG = projects.find(function(pr) { return pr.label === g.project; });
    var poCodeG = (projObjG && projObjG.projectCode) ? projObjG.projectCode : projectLabelToCode(g.project);
    var autoRef = g.project ? generatePORef(poCodeG, purchaseOrders) : '';
    var v = vendors.find(function(x) { return x.name === g.vendor; });
    var itemIds = g.items.map(function(it) { return it.id; });
    var draftBase = {
      po_ref: autoRef,
      project: g.project,
      vendorName: g.vendor,
      vendorId: v ? String(v.id) : '',
      items: itemIds,
    };
    var totals = calcTotals(itemIds);
    var draft = Object.assign(blankPO(), totals, draftBase);
    savePO(draft);
    setDetailId(draft.id);
    setView('detail');
  }

  // Revert to Draft (2026-10-21) — the PO counterpart of the proposal
  // revert. Guards in canRevertPoToDraft (utils.js). Items lose the PO
  // stamp and return to Deposit Paid (Client Approved when the PO was
  // issued past the no-deposit warning), the bypass columns clear so the
  // owner-only trigger does not reject an admin's write, and a note
  // records the revert. The issue-time document_sends snapshot stays as
  // history. The vendor was never told anything by Gilda: the person
  // reverting says whether the vendor has the PO and owns that follow-up.
  async function revertPoToDraft(p, vendorHadIt) {
    if (reverting) return;
    var check = canRevertPoToDraft(p, allItems, ctx.vendorPayments || [], ctx.receipts || []);
    if (!check.canRevert) { setRevertErr(check.reasons.join(' ')); setRevertModal(null); return; }
    setReverting(true); setRevertErr('');
    try {
      if (updateItem) {
        var ops = itemsToRevertOnPODelete(p, allItems);
        var results = await Promise.all(ops.map(function(op) { return updateItem(op.id, op.patch); }));
        var failed = results.filter(function(r) { return r === false; }).length;
        if (failed > 0) {
          setRevertErr('Revert partially failed (' + failed + ' of ' + ops.length + ' items did not save). Reload and check item states before retrying.');
          return;
        }
      }
      var who = displayNameFor(ctx.userEmail, ctx.firmSettings) || ctx.userEmail || '';
      var stamp = new Date().toISOString().slice(0, 10);
      var line = 'Reverted to Draft ' + stamp + (who ? ' by ' + who : '')
        + (vendorHadIt ? '. Vendor had received the issued PO; corrected PO or cancellation owed to the vendor.' : '. Vendor had not received it.');
      await savePO(Object.assign({}, p, {
        status: 'Draft',
        issued_at: null,
        clientPaymentStatus: null,
        bypassedBy: '',
        bypassedAt: '',
        notes: (p.notes ? p.notes + '\n' : '') + line,
      }));
      setRevertModal(null);
    } finally {
      setReverting(false);
    }
  }

  async function advanceStatus(p, opts) {
    var idx = PO_STATUSES.indexOf(p.status);
    if (idx >= PO_STATUSES.length - 1) return;
    var next = PO_STATUSES[idx + 1];
    var patch = { status: next };

    // Forward-only item status transitions — never regress an item that has already advanced.
    // Returns a promise so callers can await all per-item saves before
    // committing the PO advance to DB.
    function maybeAdvanceItem(itemId, targetItemStatus) {
      if (!updateItem) return Promise.resolve(true);
      var it = allItems.find(function(x) { return x.id === itemId; });
      if (!it) return Promise.resolve(true);
      var nextStatus = forwardOnlyItemStatusUpdate(it.status, targetItemStatus, STATUSES);
      if (!nextStatus) return Promise.resolve(true);
      return updateItem(itemId, { status: nextStatus });
    }

    if (next === 'Issued') {
      patch.issued_at = new Date().toISOString();
      // SPEC 1 — stamp bypass audit columns when the owner overrode the
      // soft warning. The trigger in 20260521_po_bypass_columns.sql will
      // reject this write unless auth.email() matches the firm owner.
      if (opts && opts.bypass) {
        patch.clientPaymentStatus = 'principal_bypass';
        patch.bypassedBy          = opts.bypass.bypassedBy;
        patch.bypassedAt          = opts.bypass.bypassedAt;
      }
      // Two-phase: stamp poNumber on each item, then advance status.
      // Combined into one updateItem call per item so we don't double-save.
      var itemPromises = (p.items || []).map(function(itemId) {
        var it = allItems.find(function(x) { return x.id === itemId; });
        if (!it) return Promise.resolve(true);
        var combinedPatch = {};
        if (p.po_ref && it.poNumber !== p.po_ref) combinedPatch.poNumber = p.po_ref;
        var nextStatus = forwardOnlyItemStatusUpdate(it.status, 'PO Issued', STATUSES);
        if (nextStatus) combinedPatch.status = nextStatus;
        if (Object.keys(combinedPatch).length === 0) return Promise.resolve(true);
        return updateItem ? updateItem(itemId, combinedPatch) : Promise.resolve(true);
      });
      var results = await Promise.all(itemPromises);
      var failed = results.filter(function(r) { return r === false; }).length;
      if (failed > 0) console.error('PO advance to Issued: ' + failed + ' item updates failed');
    } else if (next === 'Confirmed') {
      var confirmPromises = (p.items || []).map(function(itemId) { return maybeAdvanceItem(itemId, 'Ordered'); });
      var confirmResults = await Promise.all(confirmPromises);
      var confirmFailed = confirmResults.filter(function(r) { return r === false; }).length;
      if (confirmFailed > 0) console.error('PO advance to Confirmed: ' + confirmFailed + ' item updates failed');
    }
    savePO(Object.assign({}, p, patch));
  }

  // SPEC 1 — bridge between the existing IssueModal confirm and the new
  // soft-warning bypass modal. Computes shortfall: if zero (all items at
  // Deposit Paid or later), the PO issues normally. If > 0, opens the
  // bypass modal so the owner can override (or the non-owner can bail).
  async function tryIssuePO(p) {
    var itemRefs = (p.items || [])
      .map(function(id){ return allItems.find(function(it){ return it.id === id; }); })
      .filter(Boolean);
    var shortfall = computePoBypassShortfall(itemRefs, allItems, allInvoices, paymentAllocations);
    if (shortfall <= 0.005) {
      // Standard path — no warning needed.
      await advanceStatus(p);
      return { issued: true };
    }
    // Hand off to the bypass modal. The IssueModal closes first so the
    // bypass modal isn't stacked on top.
    setIssueModal(null);
    setBypassModal({ po: p, shortfall: shortfall });
    return { issued: false, awaitingBypass: true };
  }

  // ── PDF PREVIEW MODAL ─────────────────────────────────────────────────────

  if (pdfPreviewUrl) {
    // Phase 5A.2 — Live/Original toggle. Look up the most recent
    // document_sends row for this PO; if one exists, the Original radio
    // is enabled. If not (PO predates Phase 5A.2 or never made it past
    // Draft), Original is disabled with a tooltip.
    var latestPoSend = pdfPreviewPoId
      ? latestDocumentSend(ctx.documentSends || [], 'po', pdfPreviewPoId)
      : null;
    var originalAvailable = !!(latestPoSend && latestPoSend.storagePath);
    var displayUrl = pdfPreviewMode === 'original' && pdfPreviewOriginalUrl
      ? pdfPreviewOriginalUrl
      : pdfPreviewUrl;
    var backfilled = !!(latestPoSend
        && latestPoSend.payload
        && latestPoSend.payload.backfill_source);

    // Lazy-load the Original PDF from storage when the toggle first
    // flips to 'original'. Cached in state so re-toggling is free.
    async function loadOriginalSnapshot() {
      if (!latestPoSend || !latestPoSend.storagePath) return;
      if (pdfPreviewOriginalUrl) return;
      setPdfPreviewOriginalLoading(true);
      try {
        var relPath = String(latestPoSend.storagePath).replace(/^purchase-orders\//, '');
        var signed = await supabase.storage
          .from('purchase-orders')
          .createSignedUrl(relPath, 60 * 10);
        if (signed.data && signed.data.signedUrl) {
          setPdfPreviewOriginalUrl(signed.data.signedUrl);
        } else if (signed.error) {
          setPdfErr('Original snapshot fetch failed: ' + signed.error.message);
        }
      } catch (e) {
        setPdfErr('Original snapshot fetch failed: ' + (e.message || 'unknown'));
      }
      setPdfPreviewOriginalLoading(false);
    }

    function pickLive() { setPdfPreviewMode('live'); }
    function pickOriginal() {
      if (!originalAvailable) return;
      setPdfPreviewMode('original');
      loadOriginalSnapshot();
    }
    function closePreview() {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
      setPdfPreviewOriginalUrl(null);
      setPdfPreviewPoId(null);
      setPdfPreviewMode('live');
    }

    // Radio button style — minimal hairline pill, matches the Bauhaus
    // design language elsewhere. Selected: filled black; unselected:
    // hairline. Disabled: muted text + not-allowed cursor.
    function radioStyle(selected, disabled) {
      return {
        padding: '4px 12px', fontSize: 11, fontFamily: 'inherit',
        borderRadius: 12, cursor: disabled ? 'not-allowed' : 'pointer',
        border: selected ? '1.5px solid #111111' : '0.5px solid #D8D7D2',
        background: selected ? '#111111' : '#FFFFFF',
        color: disabled ? '#BBBBBB' : (selected ? '#FFFFFF' : '#555552'),
        opacity: disabled ? 0.55 : 1,
      };
    }

    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '90%', maxWidth: 900, height: '88vh', display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
          <div style={S.toolbar}>
            <span style={{ flex: 1, fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 500 }}>
              Purchase Order Preview
              {pdfPreviewMode === 'original' && backfilled && (
                <span style={{ marginLeft: 10, padding: '2px 8px', fontSize: 9, fontWeight: 700,
                  border: '0.5px solid #B9783A', borderRadius: 10, color: '#B9783A',
                  textTransform: 'uppercase', letterSpacing: '0.05em' }}
                  title="Captured retroactively from current data; may differ from what was originally sent.">
                  Retroactive
                </span>
              )}
            </span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button style={radioStyle(pdfPreviewMode === 'live', false)} onClick={pickLive}
                title="Current PO with payment progress annotations">
                Live
              </button>
              <button style={radioStyle(pdfPreviewMode === 'original', !originalAvailable)}
                onClick={pickOriginal}
                title={originalAvailable
                  ? 'The PDF the vendor received at issue time'
                  : 'Original snapshot not available — PO was issued before send-history was wired (or this PO has never been issued)'}>
                Original
              </button>
            </div>
            <button style={S.tbtnGhost} onClick={function() {
              var a = document.createElement('a'); a.href = displayUrl;
              a.download = pdfPreviewFilename;
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
            }}>↓ Download</button>
            <button style={S.tbtnGhost} onClick={closePreview}>Close</button>
          </div>
          <iframe src={displayUrl} style={{ flex: 1, border: 'none', width: '100%' }}
            title="Purchase Order PDF" />
          {pdfPreviewOriginalLoading && pdfPreviewMode === 'original' && (
            <div style={{ position: 'absolute', top: 60, left: '50%', transform: 'translateX(-50%)',
              padding: '6px 12px', background: 'rgba(0,0,0,0.7)', color: '#fff',
              borderRadius: 4, fontSize: 11 }}>
              Loading original snapshot…
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── STEP 1: METADATA FORM ──────────────────────────────────────────────────

  if (view === 'new1') {
    var step1Valid = newPO.po_ref.trim() && newPO.project && newPO.vendorName;
    var evVendors = eligibleVendors(newPO.project);

    return (
      <div style={S.wrap}>
        <div style={S.toolbar}>
          <button style={S.backBtn} onClick={function() { setView('list'); setNewPO(blankPO()); }}>
            &larr; Purchase Orders
          </button>
          <span style={S.slash}>/</span>
          <span style={S.pageTitle}>New Purchase Order</span>
          <button style={Object.assign({}, S.tbtnGhost, { opacity: step1Valid ? 1 : 0.4, cursor: step1Valid ? 'pointer' : 'default' })}
            disabled={!step1Valid}
            onClick={async function() {
              if (!step1Valid) return;
              // Replace the locally-predicted po_ref with an authoritative
              // atomic dispense from the DB so two concurrent bookkeepers
              // can't both claim the same number. Fall back to the local
              // prediction if the RPC is unavailable.
              var authoritativeRef = newPO.po_ref;
              if (dispenseDocSequence && newPO.project) {
                var projObj3 = projects.find(function(pr){return pr.label===newPO.project;});
                var poCode2 = (projObj3 && projObj3.projectCode) || projectLabelToCode(newPO.project);
                var seq = await dispenseDocSequence('PO', poCode2);
                if (seq != null) authoritativeRef = poCode2.toUpperCase() + '-PO-' + String(seq).padStart(3,'0');
              }
              var draft = Object.assign({}, newPO, { status: 'Draft', items: [], po_ref: authoritativeRef });
              savePO(draft);
              setDetailId(draft.id);
              setView('detail');
              setNewPO(blankPO());
            }}>
            Save as Draft
          </button>
          <button style={Object.assign({}, S.tbtnPrimary, { opacity: step1Valid ? 1 : 0.4, cursor: step1Valid ? 'pointer' : 'default' })}
            disabled={!step1Valid}
            onClick={function() {
              if (!step1Valid) return;
              // No dispense here. The sequence is consumed at createPO()
              // (step 2), the moment a PO row actually persists — so backing
              // out of item selection never burns a number, and re-entering
              // this step never burns two (AUDIT_UI_2026-06 S-2 side note).
              // The header on step 2 shows the locally-predicted ref as a
              // preview; the authoritative ref replaces it at creation.
              setSelectedIds([]); setView('new2');
            }}>
            Select Items &rarr;
          </button>
        </div>

        <div style={Object.assign({}, S.content, { maxWidth: 620 })}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <Lbl required>Project</Lbl>
              <select style={Object.assign({}, IS, { background: '#fff' })}
                value={newPO.project}
                onChange={function(e) {
                  var projLabel = e.target.value;
                  var projObj2 = projects.find(function(pr) { return pr.label === projLabel; });
                  var poCode = (projObj2 && projObj2.projectCode) ? projObj2.projectCode : projectLabelToCode(projLabel);
                  var autoRef = projLabel ? generatePORef(poCode, purchaseOrders) : '';
                  setNewPO(Object.assign({}, newPO, { project: projLabel, vendorId: '', vendorName: '', po_ref: autoRef }));
                }}>
                <option value="">— Select project —</option>
                {projects.map(function(pr) {
                  return <option key={pr.id} value={pr.label}>{pr.label}</option>;
                })}
              </select>
            </div>
            <div>
              <Lbl required>PO Number</Lbl>
              <input style={IS} placeholder="Select project to auto-generate"
                value={newPO.po_ref}
                onChange={function(e) { setNewPO(Object.assign({}, newPO, { po_ref: e.target.value })); }} />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            {!newPO.project
              ? (
                <>
                  <Lbl required>Vendor</Lbl>
                  <div style={Object.assign({}, IS, { background: '#F7F7F5', color: '#888880', display: 'flex', alignItems: 'center', fontStyle: 'italic' })}>
                    Select a project first
                  </div>
                </>
              )
              : evVendors.length === 0
                ? (
                  <>
                    <Lbl required>Vendor</Lbl>
                    <div style={Object.assign({}, IS, { background: '#FFF8E1', color: '#BF6800', display: 'flex', alignItems: 'center' })}>
                      No vendors with items ready for PO in this project (need Deposit Paid status, not already on a PO)
                    </div>
                  </>
                )
                : (
                  // allowAdd=false: Vendor creation is concentrated in
                  // VendorDirectory and item-creation surfaces. PO/Quote
                  // contexts must reference existing vendors so that
                  // vendor data quality decisions happen at one of two
                  // well-known surfaces.
                  <VendorPicker
                    label="Vendor"
                    required={true}
                    value={newPO.vendorName}
                    onChange={function(selectedName) {
                      var v = vendors.find(function(x) { return x.name === selectedName; });
                      setNewPO(Object.assign({}, newPO, { vendorName: selectedName, vendorId: v ? String(v.id) : '' }));
                    }}
                    vendors={evVendors}
                    items={allItems}
                    purchaseOrders={purchaseOrders}
                    allowAdd={false} />
                )
            }
          </div>

          <div>
            <Lbl>Notes</Lbl>
            <textarea style={Object.assign({}, IS, { resize: 'vertical', minHeight: 60 })}
              placeholder="Optional notes for this PO..."
              value={newPO.notes}
              onChange={function(e) { setNewPO(Object.assign({}, newPO, { notes: e.target.value })); }}
              rows={3} />
          </div>
        </div>
      </div>
    );
  }

  // ── STEP 2: ITEM PICKER ───────────────────────────────────────────────────

  if (view === 'new2') {
    var vendorNameForFilter = newPO.vendorName ||
      (vendors.find(function(v) { return String(v.id) === String(newPO.vendorId); }) || {}).name || '';
    var eligible = eligibleItems(newPO.project, vendorNameForFilter);
    var selTotals = calcTotals(selectedIds);

    // Group by room
    var roomGroups = {};
    eligible.forEach(function(it) {
      var key = it.roomNumber ? (it.roomNumber + (it.roomName ? ' \u2014 ' + it.roomName : '')) : 'No Room';
      if (!roomGroups[key]) roomGroups[key] = [];
      roomGroups[key].push(it);
    });
    var roomKeys = Object.keys(roomGroups).sort();

    function toggleItem(id) {
      setSelectedIds(function(prev) {
        return prev.indexOf(id) >= 0 ? prev.filter(function(x) { return x !== id; }) : prev.concat([id]);
      });
    }

    async function createPO() {
      if (selectedIds.length === 0) return;
      var totals = calcTotals(selectedIds, newPO);
      var p = Object.assign({}, newPO, totals, { items: selectedIds.slice() });
      // Atomic-dispense the authoritative po_ref at the moment the row is
      // actually created — mirrors Save-as-Draft. Only for genuinely NEW
      // POs: this view is reused by Edit Items, where the PO already
      // exists and already owns its ref. Falls back to the locally
      // predicted ref when the RPC is unavailable.
      var isExistingPO = purchaseOrders.some(function(x) { return x.id === p.id; });
      if (!isExistingPO && dispenseDocSequence && p.project) {
        var projObjC = projects.find(function(pr){ return pr.label === p.project; });
        var poCodeC = (projObjC && projObjC.projectCode) || projectLabelToCode(p.project);
        var seqC = await dispenseDocSequence('PO', poCodeC);
        if (seqC != null) p.po_ref = poCodeC.toUpperCase() + '-PO-' + String(seqC).padStart(3, '0');
      }
      savePO(p);
      setDetailId(p.id);
      setView('detail');
      setNewPO(blankPO());
      setSelectedIds([]);
    }

    return (
      <div style={S.wrap}>
        <div style={S.toolbar}>
          <button style={S.backBtn} onClick={function() { setView(detailId ? 'detail' : 'new1'); }}>&larr; Back</button>
          <span style={S.slash}>/</span>
          <span style={S.pageTitle}>{newPO.po_ref} — {newPO.vendorName}</span>
          {selectedIds.length > 0 && (
            <>
              <span style={{ fontSize: 11, color: '#888880', whiteSpace: 'nowrap' }}>
                {selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''}
                {' \u00b7 '}Net {fmtMoney(selTotals.total_net)}
                {' \u00b7 '}Dep. {fmtMoney(selTotals.total_deposit)}
              </span>
              <button style={S.tbtnPrimary} onClick={createPO}>Create PO</button>
            </>
          )}
        </div>

        <div style={S.content}>
          {eligible.length === 0 && (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#BBBBBB', fontSize: 13 }}>
              No items at Deposit Paid status for {newPO.vendorName} in {newPO.project}.
            </div>
          )}

          {roomKeys.map(function(rk) {
            var roomItems = roomGroups[rk];
            return (
              <div key={rk} style={{ marginBottom: 20 }}>
                <SHead>{rk}</SHead>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr>
                    <TH></TH>
                    <TH>Item</TH>
                    <TH>Product</TH>
                    <TH right>Qty</TH>
                    <TH right>Net Total</TH>
                    <TH right>Dep.%</TH>
                    <TH right>Vend. Dep.</TH>
                  </tr></thead>
                  <tbody>
                    {roomItems.map(function(it) {
                      var checked = selectedIds.indexOf(it.id) >= 0;
                      var pct = vendorDepPct(it);
                      var dep = itemDep(it);
                      return (
                        <tr key={it.id}
                          onClick={function() { toggleItem(it.id); }}
                          style={{ borderBottom: '0.5px solid #EFEFEC', cursor: 'pointer', background: checked ? '#F5F3FF' : '#FFFFFF' }}>
                          <td style={{ padding: '8px 10px', width: 28 }}>
                            <input type="checkbox" checked={checked} onChange={function() { toggleItem(it.id); }}
                              style={{ cursor: 'pointer' }} onClick={function(e) { e.stopPropagation(); }} />
                          </td>
                          <td style={{ padding: '8px 10px', fontWeight: 500, color: '#111111' }}>{it.item || 'Untitled'}</td>
                          <td style={{ padding: '8px 10px', color: '#555552' }}>{it.productName || '—'}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: '#555552' }}>{parseQty(it.qty)}{it.qtyType ? ' ' + it.qtyType : ''}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: '#111111', fontWeight: 500 }}>{fmtMoney(itemNet(it))}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: '#888880' }}>{pct}%</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: '#555552' }}>{fmtMoney(dep)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── EDIT DETAILS ──────────────────────────────────────────────────────────

  if (view === 'edit1' && detailId) {
    var editValid = !!(newPO.po_ref && newPO.po_ref.trim());
    return (
      <div style={S.wrap}>
        <div style={S.toolbar}>
          <button style={S.backBtn} onClick={function() { setView('detail'); }}>
            &larr; {newPO.po_ref || 'PO'}
          </button>
          <span style={S.slash}>/</span>
          <span style={S.pageTitle}>Edit Details</span>
          <button style={S.tbtnGhost} onClick={function() { setView('detail'); }}>Cancel</button>
          <button
            style={Object.assign({}, S.tbtnPrimary, { opacity: editValid ? 1 : 0.4, cursor: editValid ? 'pointer' : 'default' })}
            disabled={!editValid}
            onClick={function() {
              if (!editValid) return;
              savePO(newPO);
              setView('detail');
            }}>
            Save Changes
          </button>
        </div>
        <div style={Object.assign({}, S.content, { maxWidth: 620 })}>
          <div style={{ marginBottom: 12 }}>
            <Lbl required>PO Number</Lbl>
            <input style={IS} value={newPO.po_ref}
              onChange={function(e) { setNewPO(Object.assign({}, newPO, { po_ref: e.target.value })); }} />
          </div>
          <div>
            <Lbl>Notes</Lbl>
            <textarea style={Object.assign({}, IS, { resize: 'vertical', minHeight: 60 })}
              placeholder="Optional notes for this PO..."
              value={newPO.notes}
              onChange={function(e) { setNewPO(Object.assign({}, newPO, { notes: e.target.value })); }}
              rows={3} />
          </div>
        </div>
      </div>
    );
  }

  // ── DETAIL VIEW ───────────────────────────────────────────────────────────

  if (view === 'detail' && detailId) {
    var p = purchaseOrders.find(function(x) { return x.id === detailId; });
    if (!p) { setView('list'); return null; }

    var poItems = allItems.filter(function(it) { return (p.items || []).indexOf(it.id) >= 0; });
    var locked = isLocked(p);
    var nextStatus = PO_STATUSES[PO_STATUSES.indexOf(p.status) + 1];
    var nextLabel = nextStatus === 'Issued' ? 'Issue PO' : nextStatus ? 'Mark ' + nextStatus : null;

    // Group by room for display
    var dRooms = {};
    poItems.forEach(function(it) {
      var key = it.roomNumber ? (it.roomNumber + (it.roomName ? ' \u2014 ' + it.roomName : '')) : 'No Room';
      if (!dRooms[key]) dRooms[key] = [];
      dRooms[key].push(it);
    });
    var dRoomKeys = Object.keys(dRooms).sort();

    return (
      <>
      {issueModal && (
        <ConfirmationModal
          open={true}
          heading="Issue PO?"
          body={(function() {
            var name = displayNameFor(ctx.userEmail, ctx.firmSettings);
            var greet = name ? 'Hi ' + name + ' \u2014 ' : '';
            return greet + 'quick check before sending:\n\n'
              + 'Does the quote match the vendor invoice, and do both reflect what we\u2019re ordering correctly? (If necessary, check dimensions in drawings and ensure all finishes are correct)\n\n'
              + 'Once sent, this PO is finalized.';
          })()}
          confirmLabel="I’ve Verified — Send"
          cancelLabel="Cancel"
          confirming={issueConfirming}
          tone="primary"
          onCancel={function() {
            if (issueConfirming) return;
            setIssueModal(null);
          }}
          onConfirm={async function() {
            var target = issueModal && issueModal.po;
            if (!target) return;
            setIssueConfirming(true);
            try {
              var result = await tryIssuePO(target);
              if (result.issued) {
                setIssueModal(null);
                // PDF download is intentionally fire-and-forget: a failure
                // here won't roll back the status change. downloadPoPdf
                // surfaces any error into pdfErr as a non-blocking banner.
                // Phase 5A.2: captureSnapshot also uploads the PDF to
                // purchase-orders bucket + writes document_sends row.
                downloadPoPdf(target, { captureSnapshot: true });
              }
              // If !result.issued, tryIssuePO opened the bypass modal and
              // already closed the issue modal. The owner/non-owner picks
              // their path from there.
            } finally {
              setIssueConfirming(false);
            }
          }}
        />
      )}
      {revertModal && (function() {
        var rp = revertModal.po;
        var sent = latestDocumentSend(ctx.documentSends || [], 'po', rp.id);
        var revertBtn = { padding: '7px 14px', borderRadius: 5, fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: reverting ? 'default' : 'pointer', opacity: reverting ? 0.6 : 1 };
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
            data-testid="po-revert-modal-backdrop"
            onClick={function(e) { if (e.target === e.currentTarget && !reverting) setRevertModal(null); }}>
            <div role="dialog" aria-modal="true" data-testid="po-revert-modal"
              style={{ width: '100%', maxWidth: 500, background: '#fff', borderRadius: 6, boxShadow: '0 8px 40px rgba(0,0,0,0.3)', padding: 22, fontFamily: "'DM Sans', sans-serif" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#111', marginBottom: 10 }}>
                Revert {rp.po_ref || 'this PO'} to Draft?
              </div>
              <div style={{ fontSize: 13, color: '#444', lineHeight: 1.5, marginBottom: 14 }}>
                Its items go back to {rp.clientPaymentStatus === 'principal_bypass' ? 'Client Approved' : 'Deposit Paid'} and lose the PO number.
                You can then edit, re-issue, or delete it.
                {sent && sent.sentAt ? <span> Gilda recorded this PO as sent on {fmtDate(sent.sentAt)}.</span> : null}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#111', marginBottom: 10 }}>
                Has the vendor received this PO?
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button type="button" data-testid="po-revert-vendor-has-it" disabled={reverting}
                  onClick={function() { revertPoToDraft(rp, true); }}
                  style={Object.assign({}, revertBtn, { background: '#1a1a1a', color: '#fff', border: 'none', textAlign: 'left' })}>
                  Yes — I will send the vendor a corrected PO or a cancellation
                </button>
                <button type="button" data-testid="po-revert-vendor-no" disabled={reverting}
                  onClick={function() { revertPoToDraft(rp, false); }}
                  style={Object.assign({}, revertBtn, { background: '#fff', color: '#111', border: '0.5px solid #D8D7D2', textAlign: 'left' })}>
                  No — the vendor never saw it
                </button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                <button type="button" disabled={reverting} onClick={function() { setRevertModal(null); }}
                  style={Object.assign({}, revertBtn, { background: 'none', color: '#555', border: 'none' })}>
                  {reverting ? 'Reverting…' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {bypassModal && (
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.55)',
            zIndex: 1200,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
          data-testid="po-bypass-modal-backdrop"
          onClick={function(e) {
            if (e.target === e.currentTarget) setBypassModal(null);
          }}
        >
          <div
            role="dialog" aria-modal="true"
            data-testid="po-bypass-modal"
            style={{
              width: '100%', maxWidth: 480, background: '#fff',
              borderRadius: 6, boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
              padding: 22, fontFamily: "'DM Sans', sans-serif",
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111', marginBottom: 10 }}>
              Heads up: no client deposit recorded
            </div>
            <div style={{ fontSize: 13, color: '#444', lineHeight: 1.5, marginBottom: 14 }}>
              No client deposit payment is recorded for these items. The firm will
              be floating <strong>{fmtMoney(bypassModal.shortfall)}</strong> until
              the client pays. Issue anyway?
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
              <button
                type="button"
                data-testid="po-bypass-wait"
                onClick={function() { setBypassModal(null); }}
                style={{
                  padding: '7px 14px', background: '#fff',
                  border: '0.5px solid #D8D7D2', borderRadius: 5,
                  fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
                  color: '#111', cursor: 'pointer',
                }}>
                Wait — record payment first
              </button>
              {isOwner && (
                <button
                  type="button"
                  data-testid="po-bypass-issue-anyway"
                  onClick={async function() {
                    var target = bypassModal.po;
                    setBypassModal(null);
                    await advanceStatus(target, {
                      bypass: {
                        bypassedBy: ctx.userEmail || '',
                        bypassedAt: new Date().toISOString(),
                      },
                    });
                    // Phase 5A.2: bypass-path also captures the snapshot.
                    downloadPoPdf(target, { captureSnapshot: true });
                  }}
                  style={{
                    padding: '7px 14px', background: '#1a1a1a', color: '#fff',
                    border: 'none', borderRadius: 5,
                    fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                    cursor: 'pointer',
                  }}>
                  Issue anyway
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <div style={S.wrap}>
        {/* Toolbar */}
        <div style={S.toolbar}>
          <button style={S.backBtn}
            onClick={function() { setView('list'); setDeleteConfirm(false); }}>
            &larr; Purchase Orders
          </button>
          <span style={S.slash}>/</span>
          <span style={Object.assign({}, S.pageTitle, { fontFamily: 'monospace' })}>
            {p.po_ref || 'Draft'}
          </span>
          <StatusPill status={p.status} />
          <div style={{ width: '0.5px', height: 18, background: '#D8D7D2', margin: '0 2px', flexShrink: 0 }} />
          {(!locked || canManagePo) && (
            <button style={S.tbtnGhost} onClick={function() {
              setNewPO(Object.assign({}, p));
              setView('edit1');
            }}>
              Edit
            </button>
          )}
          {(!locked || canManagePo) && (
            <button style={S.tbtnGhost} onClick={function() {
              setNewPO(p);
              setSelectedIds(p.items ? p.items.slice() : []);
              setView('new2');
            }}>
              Edit Items
            </button>
          )}
          {locked && canManagePo && p.status !== 'Complete' && (
            <button style={S.tbtnGhost} data-testid="revert-po-btn" onClick={function() {
              var check = canRevertPoToDraft(p, allItems, ctx.vendorPayments || [], ctx.receipts || []);
              if (!check.canRevert) { setRevertErr(check.reasons.join(' ')); return; }
              setRevertErr('');
              setRevertModal({ po: p });
            }}>
              Revert to Draft
            </button>
          )}
          {revertErr && <span style={{ fontSize: 10, color: '#C8322A' }}>{revertErr}</span>}
          {locked && !canManagePo && (
            <span style={{ fontSize: 10, color: '#3730A3', background: '#FFFFFF', border: '0.5px solid #A5B4FC', borderRadius: 3, padding: '2px 8px', whiteSpace: 'nowrap' }}>
              Locked — owner or admin edit only
            </span>
          )}
          {nextLabel && (!locked || canManagePo) && (
            <button
              style={nextStatus === 'Issued' ? S.tbtnPrimary : S.tbtnGhost}
              data-testid={nextStatus === 'Issued' ? 'issue-po-btn' : 'advance-po-btn'}
              onClick={function() {
                // Draft → Issued is high-consequence (items lock at PO Issued
                // status, PO becomes editable only by the owner, QB push
                // becomes available). Route it through the confirmation
                // modal so every send carries an explicit quote-vs-invoice
                // verification beat. Other advances (Issued → Confirmed,
                // Confirmed → Complete) fire directly.
                if (nextStatus === 'Issued') {
                  setIssueModal({ po: p });
                } else {
                  advanceStatus(p);
                }
              }}>
              {nextLabel}
            </button>
          )}
          <button
            style={Object.assign({}, S.tbtnGhost, pdfLoading ? { opacity: 0.5, cursor: 'default' } : {})}
            disabled={pdfLoading}
            onClick={function() { previewPdf(p); }}>
            {pdfLoading ? 'Generating…' : 'Preview PDF'}
          </button>
          {pdfErr && <span style={{ fontSize: 10, color: '#C8322A' }}>{pdfErr}</span>}
          {!deleteConfirm
            ? <button style={S.tbtnDanger} onClick={function() { setDeleteConfirm(true); }}>Delete</button>
            : <>
                <span style={{ fontSize: 11, color: '#C8322A', whiteSpace: 'nowrap' }}>Delete PO?</span>
                <button style={S.tbtnDanger} onClick={function() { deletePO(p.id); }}>Confirm</button>
                <button style={S.tbtnGhost} onClick={function() { setDeleteConfirm(false); }}>Cancel</button>
              </>
          }
        </div>

        {/* Context strip */}
        <div style={S.ctxStrip}>
          {p.project && (
            <div style={S.ctxPill}>
              <span style={S.ctxLabel}>Project</span>
              <span style={{ fontSize: 11, color: '#111111' }}>{p.project}</span>
            </div>
          )}
          {p.vendorName && (
            <div style={S.ctxPill}>
              <span style={S.ctxLabel}>Vendor</span>
              <span style={{ fontSize: 11, color: '#111111', fontWeight: 500 }}>{p.vendorName}</span>
            </div>
          )}
          <div style={S.ctxPill}>
            <span style={S.ctxLabel}>Created</span>
            <span style={{ fontSize: 11, color: '#555552' }}>{fmtDate(p.created_at)}</span>
          </div>
          {p.issued_at && (
            <div style={S.ctxPill}>
              <span style={S.ctxLabel}>Issued</span>
              <span style={{ fontSize: 11, color: '#3730A3' }}>{fmtDate(p.issued_at)}</span>
            </div>
          )}
          <div style={Object.assign({}, S.ctxPill, { borderRight: 'none', marginRight: 0 })}>
            <span style={S.ctxLabel}>Items</span>
            <span style={{ fontSize: 11, color: '#555552' }}>{(p.items || []).length}</span>
          </div>
        </div>

        {/* Issued lock banner */}
        {locked && (
          <div style={{ padding: '9px 14px', background: '#EEF2FF', border: '0.5px solid #A5B4FC', fontSize: 11, color: '#3730A3', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13 }}>&#128274;</span>
            <span><strong>PO issued.</strong> This PO is locked. Items have been moved to PO Issued status. Only the owner can make changes.</span>
          </div>
        )}

        {/* Items */}
        <div style={S.content}>
          {poItems.length === 0 && (
            <div style={{ padding: '32px 0', textAlign: 'center', color: '#BBBBBB', fontSize: 13 }}>
              No items on this PO.
            </div>
          )}

          {dRoomKeys.map(function(rk) {
            var roomItems = dRooms[rk];
            var roomNet = roomItems.reduce(function(s, it) { return s + itemNet(it); }, 0);
            var roomDep = roomItems.reduce(function(s, it) { return s + itemDep(it); }, 0);
            return (
              <div key={rk} style={{ marginBottom: 20 }}>
                <SHead>{rk}</SHead>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr>
                    <TH>Item</TH>
                    <TH>SKU</TH>
                    <TH>Product</TH>
                    <TH right>Qty</TH>
                    <TH right>Net Unit</TH>
                    <TH right>Net Total</TH>
                    <TH right>Dep.%</TH>
                    <TH right>Vend. Dep.</TH>
                  </tr></thead>
                  <tbody>
                    {roomItems.flatMap(function(it) {
                      var unitNet = parsePrice(it.netPrice);
                      var pct = vendorDepPct(it);
                      var itemId = it.id;
                      var isExp = !!expandedItems[itemId];
                      function toggleExpand() {
                        setExpandedItems(function(prev) {
                          var n = Object.assign({}, prev);
                          if (n[itemId]) { delete n[itemId]; } else { n[itemId] = true; }
                          return n;
                        });
                      }
                      var mainRow = (
                        <tr key={itemId}
                            onClick={toggleExpand}
                            style={{ borderBottom: isExp ? 'none' : '0.5px solid #EFEFEC', cursor: 'pointer', background: isExp ? '#EEF2FF' : '#FFFFFF' }}
                            onMouseEnter={function(e) { if (!isExp) e.currentTarget.style.background = '#F7F7F5'; }}
                            onMouseLeave={function(e) { e.currentTarget.style.background = isExp ? '#EEF2FF' : '#FFFFFF'; }}>
                          <td style={{ padding: '8px 10px', fontWeight: 500, color: '#111111' }}>
                            <span style={{ fontSize: 10, color: '#888880', marginRight: 6, userSelect: 'none' }}>{isExp ? '▾' : '▸'}</span>
                            {it.item || 'Untitled'}
                          </td>
                          <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 11, color: '#555552', whiteSpace: 'nowrap' }}>
                            {it.itemNumber
                              ? it.itemNumber
                              : <button
                                  style={{ height: 22, padding: '0 8px', borderRadius: 4, border: '0.5px solid #D8D7D2', background: skuAssigning[it.id] ? '#F7F7F5' : '#FFFFFF', cursor: skuAssigning[it.id] ? 'default' : 'pointer', fontSize: 10, color: '#888880', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                                  disabled={!!skuAssigning[it.id]}
                                  onClick={function(e) { e.stopPropagation(); assignSku(it); }}>
                                  {skuAssigning[it.id] ? 'Assigning…' : 'Assign SKU'}
                                </button>
                            }
                          </td>
                          <td style={{ padding: '8px 10px', color: '#555552' }}>{it.productName || '—'}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: '#555552' }}>{parseQty(it.qty)}{it.qtyType ? ' ' + it.qtyType : ''}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: '#555552' }}>{fmtMoney(unitNet)}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500, color: '#111111' }}>{fmtMoney(itemNet(it))}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: '#888880' }}>{pct}%</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: '#555552' }}>{fmtMoney(itemDep(it))}</td>
                        </tr>
                      );
                      if (!isExp) return [mainRow];
                      return [mainRow, (
                        <tr key={itemId + '-detail'} style={{ borderBottom: '0.5px solid #EFEFEC', background: '#EEF2FF' }}>
                          <td colSpan={8} style={{ padding: '4px 12px 14px 32px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '8px 24px', fontSize: 11 }}>
                              <div><span style={{ fontSize: 10, color: '#888880', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Status</span><br /><span style={{ color: '#111111' }}>{it.status || '—'}</span></div>
                              {it.vendor && <div><span style={{ fontSize: 10, color: '#888880', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Vendor</span><br /><span style={{ color: '#555552' }}>{it.vendor}</span></div>}
                              {it.model && <div><span style={{ fontSize: 10, color: '#888880', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Model</span><br /><span style={{ color: '#555552' }}>{it.model}</span></div>}
                              {it.dimensions && <div><span style={{ fontSize: 10, color: '#888880', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Dimensions</span><br /><span style={{ color: '#555552' }}>{it.dimensions}</span></div>}
                              {it.finish && <div><span style={{ fontSize: 10, color: '#888880', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Finish</span><br /><span style={{ color: '#555552' }}>{it.finish}</span></div>}
                              {it.leadTime && <div><span style={{ fontSize: 10, color: '#888880', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Lead Time</span><br /><span style={{ color: '#555552' }}>{it.leadTime}</span></div>}
                              {it.specNotes && <div style={{ gridColumn: '1 / -1' }}><span style={{ fontSize: 10, color: '#888880', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Spec Notes</span><br /><span style={{ color: '#555552' }}>{it.specNotes}</span></div>}
                              {it.notes && <div style={{ gridColumn: '1 / -1' }}><span style={{ fontSize: 10, color: '#888880', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Notes</span><br /><span style={{ color: '#555552' }}>{it.notes}</span></div>}
                            </div>
                          </td>
                        </tr>
                      )];
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '0.5px solid #D8D7D2' }}>
                      <td colSpan={5} style={{ padding: '6px 10px', textAlign: 'right', fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880' }}>
                        Room subtotal
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 500, color: '#111111' }}>{fmtMoney(Math.round(roomNet * 100) / 100)}</td>
                      <td></td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#555552' }}>{fmtMoney(Math.round(roomDep * 100) / 100)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })}

          {/* Grand totals — calculated live so fees are always current */}
          {poItems.length > 0 && (function() {
            var liveNet = poItems.reduce(function(s, it) { return s + itemNet(it); }, 0);
            var liveDep = poItems.reduce(function(s, it) { return s + itemDep(it); }, 0);
            return (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, marginBottom: 20 }}>
                <div style={{ minWidth: 280, borderTop: '0.5px solid #D8D7D2', paddingTop: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: '#555552' }}>
                    <span>Net Total (product)</span>
                    <span style={{ fontWeight: 500, color: '#111111' }}>{fmtMoney(Math.round(liveNet * 100) / 100)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 500, color: '#111111', borderTop: '0.5px solid #EFEFEC', paddingTop: 6 }}>
                    <span>Vendor Deposit Due</span>
                    <span>{fmtMoney(Math.round(liveDep * 100) / 100)}</span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Notes */}
          {p.notes && (
            <div style={{ marginBottom: 20, padding: '10px 14px', background: '#F7F7F5', border: '0.5px solid #D8D7D2', borderRadius: 4 }}>
              <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', marginBottom: 4 }}>Notes</div>
              <div style={{ fontSize: 12, color: '#555552' }}>{p.notes}</div>
            </div>
          )}
        </div>
      </div>
      </>
    );
  }

  // ── LIST VIEW ─────────────────────────────────────────────────────────────

  // Items awaiting a PO — eligible status, not already on any PO
  var allPoItemIds = new Set();
  purchaseOrders.forEach(function(po) {
    (po.items || []).forEach(function(id) { allPoItemIds.add(id); });
  });
  var pendingItems = allItems.filter(function(it) {
    return isBillable(it) && isPoEligibleStatus(it.status) && !allPoItemIds.has(it.id);
  });
  var pendingGroups = {};
  pendingItems.forEach(function(it) {
    var key = (it.project || '') + '||' + (it.vendor || '');
    if (!pendingGroups[key]) pendingGroups[key] = { project: it.project || '', vendor: it.vendor || '', items: [] };
    pendingGroups[key].items.push(it);
  });
  var pendingGroupKeys = Object.keys(pendingGroups).sort();
  var filterProjects = Array.from(new Set(
    pendingItems.map(function(it) { return it.project || ''; })
      .concat(purchaseOrders.map(function(po) { return po.project || ''; }))
      .filter(Boolean)
  )).sort();
  var visiblePendingGroupKeys = projFilter
    ? pendingGroupKeys.filter(function(key) { return pendingGroups[key].project === projFilter; })
    : pendingGroupKeys;
  var visiblePOs = (projFilter
    ? purchaseOrders.filter(function(po) { return po.project === projFilter; })
    : purchaseOrders)
    .filter(function(po) {
      // Status pills: In Process = Draft/Issued/Confirmed; Complete = Complete.
      if (statusFilter === 'inprocess' && po.status === 'Complete') return false;
      if (statusFilter === 'complete' && po.status !== 'Complete') return false;
      return true;
    })
    .filter(function(po) {
      // Search across ref, vendor, project, and the items' product names.
      var q = poSearch.trim().toLowerCase();
      if (!q) return true;
      var hay = [po.po_ref, po.vendorName, po.project, productLabel(po.items)]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.indexOf(q) >= 0;
    })
    .slice()
    .sort(function(a, b) { return extractPOSeq(a.po_ref) - extractPOSeq(b.po_ref); });
  var visiblePendingItemCount = visiblePendingGroupKeys.reduce(function(s, key) { return s + pendingGroups[key].items.length; }, 0);
  var draftCount = visiblePOs.filter(function(p) { return p.status === 'Draft'; }).length;

  return (
    <div style={S.wrap}>
      <div style={S.toolbar}>
        <span style={S.pageTitle}>Purchase Orders</span>
        {draftCount > 0 && (
          <span style={{ fontSize: 10, color: '#888880', letterSpacing: '0.07em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            {draftCount} draft{draftCount !== 1 ? 's' : ''}
          </span>
        )}
        <button style={S.tbtnPrimary}
          onClick={function() { setNewPO(blankPO()); setView('new1'); }}>
          + New PO
        </button>
      </div>

      {filterProjects.length > 0 && (
        <div style={{display:'flex',alignItems:'center',height:36,padding:'0 14px',gap:6,overflowX:'auto',borderBottom:'0.5px solid #D8D7D2',background:'#FFFFFF',flexShrink:0}}>
          <button
            onClick={function() { setProjFilter(''); }}
            style={{height:24,padding:'0 10px',borderRadius:12,border:projFilter===''?'1.5px solid #111111':'0.5px solid #D8D7D2',background:projFilter===''?'#111111':'#FFFFFF',color:projFilter===''?'#FFFFFF':'#888880',fontSize:11,fontWeight:500,cursor:'pointer',fontFamily:'inherit',flexShrink:0,whiteSpace:'nowrap'}}>
            All
          </button>
          {filterProjects.map(function(proj) {
            var active = projFilter === proj;
            return (
              <button key={proj}
                onClick={function() { setProjFilter(active ? '' : proj); }}
                style={{height:24,padding:'0 10px',borderRadius:12,border:active?'1.5px solid #111111':'0.5px solid #D8D7D2',background:active?'#111111':'#FFFFFF',color:active?'#FFFFFF':'#888880',fontSize:11,fontWeight:500,cursor:'pointer',fontFamily:'inherit',flexShrink:0,whiteSpace:'nowrap'}}>
                {proj}
              </button>
            );
          })}
        </div>
      )}

      {/* Status pills + search (2026-07-12, Cimon). In Process = Draft +
          Issued + Confirmed; Complete = Complete. Search matches ref,
          vendor, project, and item names. autoCorrect/autoCapitalize/
          spellCheck OFF — Safari was "correcting" search terms ("HEN" →
          "Him") and wiping live searches mid-selection. */}
      <div style={{display:'flex',alignItems:'center',height:36,padding:'0 14px',gap:6,borderBottom:'0.5px solid #D8D7D2',background:'#FFFFFF',flexShrink:0}}>
        {[['all','All'],['inprocess','In Process'],['complete','Complete']].map(function(opt) {
          var val = opt[0]; var label = opt[1];
          var active = statusFilter === val;
          return (
            <button key={val}
              data-testid={'po-status-pill-' + val}
              onClick={function() { setStatusFilter(val); }}
              style={{height:24,padding:'0 10px',borderRadius:12,border:active?'1.5px solid #111111':'0.5px solid #D8D7D2',background:active?'#111111':'#FFFFFF',color:active?'#FFFFFF':'#888880',fontSize:11,fontWeight:500,cursor:'pointer',fontFamily:'inherit',flexShrink:0,whiteSpace:'nowrap'}}>
              {label}
            </button>
          );
        })}
        <input
          type="text"
          value={poSearch}
          onChange={function(e) { setPoSearch(e.target.value); }}
          placeholder="Search ref, vendor, item…"
          autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
          data-testid="po-search"
          style={{marginLeft:'auto',height:26,width:240,padding:'0 10px',border:'0.5px solid #D8D7D2',borderRadius:13,fontSize:11,fontFamily:'inherit',color:'#111111',boxSizing:'border-box'}} />
      </div>

      <div style={S.content}>

        {/* ── READY TO PURCHASE ──────────────────────────────────────────── */}
        {visiblePendingGroupKeys.length > 0 && (function() {
          // Per-item installation rule (since 2026-06-03): itemDep checks
          // each item's fees.installation, no per-group vendor lookup
          // needed.
          var pendingTotalNet = visiblePendingGroupKeys.reduce(function(s, key) { return s + pendingGroups[key].items.reduce(function(s2, it) { return s2 + itemNet(it); }, 0); }, 0);
          var pendingTotalDep = visiblePendingGroupKeys.reduce(function(s, key) {
            var g = pendingGroups[key];
            return s + g.items.reduce(function(s2, it) { return s2 + itemDep(it); }, 0);
          }, 0);
          return (
            <div style={{ marginBottom: 32 }}>
              <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', marginBottom: 8 }}>
                Ready to Purchase — {visiblePendingItemCount} item{visiblePendingItemCount !== 1 ? 's' : ''} awaiting PO
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr>
                  {!projFilter && <TH>Project</TH>}
                  <TH>Vendor</TH>
                  <TH>Product</TH>
                  <TH right>Items</TH>
                  <TH right>Net Total</TH>
                  <TH right>Vend. Deposit</TH>
                  <TH></TH>
                </tr></thead>
                <tbody>
                  {visiblePendingGroupKeys.map(function(key) {
                    var g = pendingGroups[key];
                    var net = g.items.reduce(function(s, it) { return s + itemNet(it); }, 0);
                    var dep = g.items.reduce(function(s, it) { return s + itemDep(it); }, 0);
                    return (
                      <tr key={key}
                          onClick={function() { createDraftAndNavigate(g); }}
                          style={{ borderBottom: '0.5px solid #EFEFEC', cursor: 'pointer', background: '#FFFFFF' }}
                          onMouseEnter={function(e) { e.currentTarget.style.background = '#F7F7F5'; }}
                          onMouseLeave={function(e) { e.currentTarget.style.background = '#FFFFFF'; }}>
                        {!projFilter && <td style={{ padding: '8px 10px', color: '#555552' }}>{g.project || '—'}</td>}
                        <td style={{ padding: '8px 10px', fontWeight: 500, color: '#111111' }}>{g.vendor || '—'}</td>
                        <td style={{ padding: '8px 10px', color: '#555552', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{productLabel(g.items)}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', color: '#555552' }}>{g.items.length}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500, color: '#111111' }}>{fmtMoney(Math.round(net * 100) / 100)}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', color: '#555552' }}>{fmtMoney(Math.round(dep * 100) / 100)}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, color: '#888880' }}>Open &rarr;</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '0.5px solid #D8D7D2' }}>
                    <td colSpan={projFilter ? 3 : 4} style={{ padding: '7px 10px', textAlign: 'right', fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880' }}>
                      {visiblePendingGroupKeys.length} vendor{visiblePendingGroupKeys.length !== 1 ? 's' : ''}
                    </td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 500, color: '#111111', fontSize: 11 }}>{fmtMoney(Math.round(pendingTotalNet * 100) / 100)}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: '#555552', fontSize: 11 }}>{fmtMoney(Math.round(pendingTotalDep * 100) / 100)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          );
        })()}

        {/* ── EXISTING POs ───────────────────────────────────────────────── */}
        {visiblePOs.length === 0 && visiblePendingGroupKeys.length === 0 && (
          <div style={{ padding: '64px 0', textAlign: 'center', color: '#BBBBBB', fontSize: 13 }}>
            No purchase orders yet. Click "+ New PO" to create one.
          </div>
        )}

        {visiblePOs.length > 0 && (function() {
          // Live totals (compute from current item state) -- the stored
          // po.total_net / po.total_deposit go stale when items are
          // re-priced after PO save.
          var listPoNet     = visiblePOs.reduce(function(s, po) { return s + poNetLive(po, allItems); }, 0);
          // poDepositLive checks each item's fees.installation directly
          // (2026-06-03 per-item rule). Column label is "Vend. Deposit"
          // → matches PDF deposit total.
          var listPoDeposit = visiblePOs.reduce(function(s, po) { return s + poDepositLive(po, allItems); }, 0);
          return (
            <>
              {visiblePendingGroupKeys.length > 0 && (
                <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', marginBottom: 8 }}>
                  Purchase Orders
                </div>
              )}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr>
                  <TH>PO Ref</TH>
                  {!projFilter && <TH>Project</TH>}
                  <TH>Vendor</TH>
                  <TH>Product</TH>
                  <TH right>Items</TH>
                  <TH right>Net Total</TH>
                  <TH right>Vend. Deposit</TH>
                  <TH>Status</TH>
                  <TH>Date Sent</TH>
                  <TH>Created</TH>
                  <TH></TH>
                </tr></thead>
                <tbody>
                  {visiblePOs.map(function(po) {
                    return (
                      <tr key={po.id}
                        onClick={function() { setDetailId(po.id); setDeleteConfirm(false); setView('detail'); }}
                        style={{ borderBottom: '0.5px solid #EFEFEC', cursor: 'pointer', background: '#FFFFFF' }}
                        onMouseEnter={function(e) { e.currentTarget.style.background = '#F7F7F5'; }}
                        onMouseLeave={function(e) { e.currentTarget.style.background = '#FFFFFF'; }}>
                        <td style={{ padding: '10px 10px', fontFamily: 'monospace', fontSize: 11, color: '#111111', fontWeight: 500 }}>{po.po_ref || '—'}</td>
                        {!projFilter && <td style={{ padding: '10px 10px', color: '#555552' }}>{po.project || '—'}</td>}
                        <td style={{ padding: '10px 10px', color: '#555552', fontWeight: 500 }}>{po.vendorName || '—'}</td>
                        <td style={{ padding: '10px 10px', color: '#555552', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{productLabel(po.items)}</td>
                        <td style={{ padding: '10px 10px', textAlign: 'right', color: '#555552' }}>{(po.items || []).length}</td>
                        <td style={{ padding: '10px 10px', textAlign: 'right', fontWeight: 500, color: '#111111' }}>{fmtMoney(poNetLive(po, allItems))}</td>
                        <td style={{ padding: '10px 10px', textAlign: 'right', color: '#555552' }}>{fmtMoney(poDepositLive(po, allItems))}</td>
                        <td style={{ padding: '10px 10px' }}><StatusPill status={po.status} /></td>
                        <td style={{ padding: '10px 10px', color: '#888880' }} data-testid={'po-date-sent-' + po.id}>
                          {po.status === 'Draft' ? <span style={{ color: '#bbb' }}>—</span> : fmtDate(po.issued_at)}
                        </td>
                        <td style={{ padding: '10px 10px', color: '#888880' }}>{fmtDate(po.created_at)}</td>
                        <td style={{ padding: '10px 10px', textAlign: 'right', color: '#888880', fontSize: 11 }}>Open &rarr;</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '0.5px solid #D8D7D2' }}>
                    <td colSpan={projFilter ? 4 : 5} style={{ padding: '7px 10px', textAlign: 'right', fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880' }}>
                      {visiblePOs.length} PO{visiblePOs.length !== 1 ? 's' : ''}
                    </td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 500, color: '#111111', fontSize: 11 }}>{fmtMoney(Math.round(listPoNet * 100) / 100)}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: '#555552', fontSize: 11 }}>{fmtMoney(Math.round(listPoDeposit * 100) / 100)}</td>
                    <td colSpan={4}></td>
                  </tr>
                </tfoot>
              </table>
            </>
          );
        })()}
      </div>
    </div>
  );
}
